/**
 * Calls Page - Sales call recordings from Fireflies.ai
 *
 * Shows:
 * - Search bar to filter calls by title
 * - Data table with pagination showing calls from Fireflies
 * - Sync button to fetch latest calls from Fireflies API
 * - Modal with video player, transcript, and AI summary
 */

import { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, Phone, Play, Clock, Calendar } from "lucide-react";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CallDetailModal } from "../components/CallDetailModal";

// Types
interface FirefliesTranscript {
  _id: string;
  firefliesId: string;
  title: string;
  date: number;
  duration: number;
  outcome: string | null;
}

interface TranscriptListResponse {
  items: FirefliesTranscript[];
  totalCount: number;
}

// Format timestamp to readable date
const formatDate = (ms: number | undefined): string => {
  if (!ms || !Number.isFinite(ms)) return "N/A";
  const d = new Date(ms);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Format duration in seconds to HH:MM:SS or MM:SS
const formatDuration = (secs: number | undefined): string => {
  if (!secs || !Number.isFinite(secs)) return "N/A";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  const parts = [
    h > 0 ? String(h).padStart(2, "0") : null,
    String(m).padStart(2, "0"),
    String(s).padStart(2, "0"),
  ].filter(Boolean) as string[];
  return parts.join(":");
};

export function Calls() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [items, setItems] = useState<FirefliesTranscript[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Open call detail modal
  const openCallDetail = (firefliesId: string) => {
    setSelectedCallId(firefliesId);
    setIsModalOpen(true);
  };

  // Close call detail modal
  const closeCallDetail = () => {
    setIsModalOpen(false);
    setSelectedCallId(null);
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch transcripts
  const fetchTranscripts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        search: debouncedSearch,
      });

      const res = await fetch(`/api/fireflies/list?${params.toString()}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || `Failed to fetch: ${res.status}`);
      }

      const json = (await res.json()) as TranscriptListResponse;
      setItems(json.items);
      setTotalCount(json.totalCount);
    } catch (e) {
      console.error("Error fetching transcripts:", e);
      setError(e instanceof Error ? e.message : "Failed to fetch calls");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchTranscripts();
  }, [fetchTranscripts]);

  // Sync calls from Fireflies
  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setError(null);

      const res = await fetch("/api/fireflies/sync-incremental?pages=3", {
        method: "POST",
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to sync");
      }

      const result = await res.json();
      console.log(`Synced ${result.upserted} transcripts`);

      // Refresh the list
      await fetchTranscripts();
    } catch (e) {
      console.error("Error syncing transcripts:", e);
      setError(e instanceof Error ? e.message : "Failed to sync calls");
    } finally {
      setIsSyncing(false);
    }
  };

  // Table columns
  const columns: ColumnDef<FirefliesTranscript>[] = [
    {
      id: "title",
      header: "Title",
      cell: (row) => (
        <button
          onClick={() => openCallDetail(row.firefliesId)}
          className="font-medium text-sm pr-4 max-w-md truncate text-left hover:text-[var(--brand-primary)] transition-colors cursor-pointer"
        >
          {row.title}
        </button>
      ),
    },
    {
      id: "date",
      header: "Date",
      cell: (row) => (
        <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
          <Calendar className="h-3.5 w-3.5 hidden sm:block" />
          {formatDate(row.date)}
        </div>
      ),
    },
    {
      id: "duration",
      header: "Duration",
      cell: (row) => (
        <div className="flex items-center gap-2 text-sm font-mono text-muted-foreground">
          <Clock className="h-3.5 w-3.5 hidden sm:block" />
          {formatDuration(row.duration)}
        </div>
      ),
    },
    {
      id: "outcome",
      header: "Outcome",
      cell: (row) => {
        const outcome = row.outcome;
        if (!outcome) {
          return <span className="text-xs text-muted-foreground/40 italic">—</span>;
        }
        const lower = outcome.toLowerCase();
        let colorClass = "bg-muted text-muted-foreground";
        if (lower.startsWith("closed")) colorClass = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
        else if (lower.includes("no show") || lower.includes("no-show") || lower.includes("noshow")) colorClass = "bg-red-500/15 text-red-500";
        else if (lower === "cancelled" || lower === "canceled" || lower === "rescheduled") colorClass = "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400";
        else if (lower.includes("mrr")) colorClass = "bg-blue-500/15 text-blue-500";
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${colorClass}`}>
            {outcome}
          </span>
        );
      },
    },
    {
      id: "action",
      header: "Action",
      cell: (row) => (
        <Button
          size="sm"
          onClick={() => openCallDetail(row.firefliesId)}
          className="bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-medium px-3 sm:px-4 hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 transition-all"
        >
          <Play className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">View Call</span>
        </Button>
      ),
      className: "text-right",
      headerClassName: "text-right",
    },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="opacity-0 animate-fade-in-up">
        <h2 className="text-2xl font-bold mb-2">Sales Calls</h2>
        <p className="text-muted-foreground">
          Review recorded sales calls from Fireflies.ai
        </p>
      </div>

      {/* Search & Sync Bar */}
      <div className="bg-card rounded-2xl p-4 sm:p-6 shadow-brand opacity-0 animate-fade-in-up stagger-1">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Search Input */}
          <div className="relative flex-1 w-full sm:max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search calls by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-secondary border-border h-11"
            />
          </div>

          {/* Sync Button */}
          <Button
            onClick={handleSync}
            disabled={isSyncing}
            variant="outline"
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing..." : "Sync Calls"}
          </Button>
        </div>

        {/* Stats */}
        <div className="mt-4 flex items-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-[var(--brand-primary)]" />
            <span>{totalCount.toLocaleString()} total calls</span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-xl p-4 opacity-0 animate-fade-in-up">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchTranscripts}
            className="mt-3"
          >
            Try Again
          </Button>
        </div>
      )}

      {/* Calls Table */}
      <div className="opacity-0 animate-fade-in-up stagger-2">
        <DataTable
          data={items}
          columns={columns}
          rowKey="_id"
          loading={isLoading}
          emptyMessage={
            debouncedSearch
              ? `No calls found matching "${debouncedSearch}"`
              : "No calls found. Click 'Sync Calls' to fetch from Fireflies."
          }
          serverPagination={{
            currentPage: page,
            pageSize: pageSize,
            totalItems: totalCount,
            totalPages: totalPages,
            pageSizeOptions: [10, 25, 50],
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
        />
      </div>

      {/* Call Detail Modal */}
      <CallDetailModal
        isOpen={isModalOpen}
        onClose={closeCallDetail}
        firefliesId={selectedCallId}
      />
    </div>
  );
}

export default Calls;
